// PrivateMarketGuide.js — InvestorQ
// Interactive usage guide modal for the Private Sports Market
// Covers: Tier comparison, Org flow, Deal room walkthrough, Valuations

import { useState, useEffect, useRef } from "react";

// ── Data ──────────────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: "A",
    icon: "◎",
    name: "Market Intelligence",
    color: "#00d4aa",
    bg: "rgba(0,212,170,0.08)",
    border: "rgba(0,212,170,0.2)",
    tag: "FREE · INSTANT",
    tagColor: "#00d4aa",
    headline: "Your org gets listed immediately with an AI-generated editorial valuation.",
    forOrg: [
      "Submit the application form — takes 5 minutes",
      "No documents required",
      "Tile appears live on Private Market instantly",
      "AI generates editorial valuation using public market data",
      "Editorial badge + valuation range displayed on tile",
      "One-click upgrade to Tier B when ready",
    ],
    forInvestor: [
      "Browse editorial estimates for any org",
      "Add to GM Portfolio for tracking",
      "Watch intelligence feed for market signals",
      "Shadow token simulation (not a security)",
      "No deal room access at this tier",
    ],
    badge: "EDITORIAL",
    badgeStyle: { background: "rgba(0,212,170,0.12)", color: "#00d4aa", border: "1px solid rgba(0,212,170,0.3)" },
  },
  {
    id: "B",
    icon: "◈",
    name: "Certified Intelligence",
    color: "#7c6fff",
    bg: "rgba(124,111,255,0.08)",
    border: "rgba(124,111,255,0.2)",
    tag: "$5K/YEAR · 2-3 WEEKS",
    tagColor: "#7c6fff",
    headline: "NDA-gated verified financials, deal room access, and enhanced investor confidence.",
    forOrg: [
      "Request upgrade from Tier A tile",
      "InvestorQ reviews application (2-3 days)",
      "NDA sent for e-signature",
      "Secure upload link sent after NDA executed",
      "Upload financials, cap table, org deck",
      "Admin reviews → Certified badge granted",
      "Valuation upgrades from editorial to verified range",
      "Deal Room opens with your documents",
    ],
    forInvestor: [
      "◈ CERTIFIED badge signals verified org",
      "Tighter, more confident valuation range",
      "Deal Room: access financial statements under NDA",
      "NDA request flow directly in the platform",
      "Investor interest registration",
      "Priority placement in market feed",
    ],
    badge: "CERTIFIED",
    badgeStyle: { background: "rgba(124,111,255,0.12)", color: "#7c6fff", border: "1px solid rgba(124,111,255,0.3)" },
  },
  {
    id: "C",
    icon: "◆",
    name: "Live Investment Raise",
    color: "#f5a623",
    bg: "rgba(245,166,35,0.08)",
    border: "rgba(245,166,35,0.2)",
    tag: "5% SUCCESS FEE · 4-6 WEEKS",
    tagColor: "#f5a623",
    headline: "Real Reg CF or Reg A+ raise via licensed broker-dealer partner.",
    forOrg: [
      "Active Tier B certification required",
      "Broker-dealer agreement (DealMaker or Republic)",
      "SEC Form C filed with funding portal",
      "InvestorQ embeds live raise widget on tile",
      "Real-time investor count + raised amount",
      "Post-raise cap table update",
    ],
    forInvestor: [
      "◆ LIVE RAISE badge on tile",
      "Real investment — not simulation",
      "Embedded checkout via DealMaker/Republic",
      "KYC + accreditation handled by BD partner",
      "Investment limits apply (Reg CF: $5M max)",
      "Illiquid — private market, not publicly traded",
    ],
    badge: "LIVE RAISE",
    badgeStyle: { background: "rgba(245,166,35,0.12)", color: "#f5a623", border: "1px solid rgba(245,166,35,0.3)" },
  },
];

const ORG_STEPS = [
  { step: "01", title: "Fill the Application", icon: "📋", color: "#00d4aa",
    desc: "Submit your org profile in the Application Portal. Takes 5 minutes. No documents required for Tier A.", tier: "A" },
  { step: "02", title: "Tile Goes Live", icon: "🏆", color: "#00d4aa",
    desc: "Your organization appears instantly on the Private Market with an AI-generated editorial valuation and the EDITORIAL badge.", tier: "A" },
  { step: "03", title: "Request Tier B Upgrade", icon: "⬆️", color: "#7c6fff",
    desc: "Click 'Upgrade to Tier B' on your tile. InvestorQ reviews your application within 2-3 business days.", tier: "B" },
  { step: "04", title: "Sign the NDA", icon: "🔒", color: "#7c6fff",
    desc: "We send a Mutual NDA for e-signature. This protects your financial information and is required for document submission.", tier: "B" },
  { step: "05", title: "Upload Documents", icon: "📂", color: "#7c6fff",
    desc: "You receive a secure upload link via email. Upload your financials, cap table, and org overview directly in the portal.", tier: "B" },
  { step: "06", title: "Get Certified", icon: "◈", color: "#7c6fff",
    desc: "InvestorQ reviews your documents. Once approved, your tile upgrades to CERTIFIED with a verified valuation range and open Deal Room.", tier: "B" },
  { step: "07", title: "Launch a Live Raise", icon: "◆", color: "#f5a623",
    desc: "Ready to raise capital? We connect you with DealMaker or Republic for a compliant Reg CF or Reg A+ raise embedded on your tile.", tier: "C" },
];

const VALUATION_METHODS = [
  { icon: "①", name: "Revenue Multiple", weight: "25%", color: "#00d4aa",
    desc: "Sport-appropriate EV/Revenue multiple applied to your revenue band. Based on comparable league and franchise transactions from Sportico, Forbes, and Houlihan Lokey." },
  { icon: "②", name: "Comparable Transactions", weight: "35%", color: "#7c6fff",
    desc: "Cross-referenced against actual sports M&A transactions from 2022-2025. The highest-weight method — real deals set the market." },
  { icon: "③", name: "Brand & Fan Equity", weight: "20%", color: "#f5a623",
    desc: "ISO 10668 brand model incorporating attendance, social following, streaming views, and sponsorship relationships." },
  { icon: "④", name: "DCF Forward View", weight: "20%", color: "#ff6b9d",
    desc: "5-year free cash flow projection with a sport-appropriate discount rate and terminal growth assumption." },
];

const DEAL_ROOM_STEPS = [
  { icon: "🔐", title: "Enter the Deal Room", desc: "Click 'Deal Room' tab on any Tier B asset. You'll see a Confidentiality Acknowledgment gate — review and accept the terms." },
  { icon: "📧", title: "Request NDA Access", desc: "Enter your name and email to request NDA access. InvestorQ emails you the Mutual NDA for e-signature within 2 business days." },
  { icon: "✍️", title: "Execute the NDA", desc: "Sign the NDA electronically. Your email is registered and NDA-gated documents unlock automatically." },
  { icon: "📊", title: "Access Documents", desc: "View audited financials, franchise agreements, viewership reports, and valuation models. Documents open directly in your browser." },
];

// ── Section components ────────────────────────────────────────────────────────

function TierComparisonSection() {
  const [activeTier, setActiveTier] = useState("B");
  const tier = TIERS.find(t => t.id === activeTier);

  return (
    <div>
      {/* Tier selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {TIERS.map(t => (
          <button key={t.id} onClick={() => setActiveTier(t.id)} style={{
            flex: 1, padding: "12px 8px", borderRadius: 12,
            border: `1px solid ${activeTier === t.id ? t.color : "rgba(255,255,255,0.08)"}`,
            background: activeTier === t.id ? t.bg : "rgba(255,255,255,0.02)",
            color: activeTier === t.id ? t.color : "#555",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em" }}>TIER {t.id}</div>
            <div style={{ fontSize: 9, color: activeTier === t.id ? t.color : "#475569", marginTop: 2 }}>{t.name}</div>
          </button>
        ))}
      </div>

      {/* Tier detail */}
      <div style={{
        background: tier.bg, border: `1px solid ${tier.border}`,
        borderRadius: 16, padding: "20px 22px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 22, color: tier.color }}>{tier.icon}</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>Tier {tier.id} — {tier.name}</span>
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5, maxWidth: 380 }}>{tier.headline}</div>
          </div>
          <div style={{ ...tier.badgeStyle, fontSize: 9, fontWeight: 900, padding: "3px 10px", borderRadius: 6, letterSpacing: "0.1em", flexShrink: 0 }}>
            {tier.badge}
          </div>
        </div>

        <div style={{ fontSize: 9, color: tier.color, fontWeight: 800, letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>
          {tier.tag}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>For Organizations</div>
            {tier.forOrg.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
                <span style={{ color: tier.color, fontSize: 11, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>For Investors</div>
            {tier.forInvestor.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
                <span style={{ color: tier.color, fontSize: 11, flexShrink: 0 }}>◆</span>
                <span style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrgFlowSection() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div>
      {/* Timeline */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
        {ORG_STEPS.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <button onClick={() => setActiveStep(i)} style={{
              width: 36, height: 36, borderRadius: "50%",
              border: `2px solid ${activeStep === i
                ? s.color
                : i < activeStep ? s.color : "rgba(255,255,255,0.1)"}`,
              background: activeStep === i ? s.color : i < activeStep ? `${s.color}22` : "rgba(255,255,255,0.03)",
              color: activeStep === i ? "#000" : i < activeStep ? s.color : "#475569",
              cursor: "pointer", fontSize: 11, fontWeight: 900,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s", flexShrink: 0,
            }}>
              {i < activeStep ? "✓" : s.step}
            </button>
            {i < ORG_STEPS.length - 1 && (
              <div style={{
                height: 2, width: 24, flexShrink: 0,
                background: i < activeStep ? ORG_STEPS[i].color : "rgba(255,255,255,0.08)",
                transition: "background 0.3s",
              }} />
            )}
          </div>
        ))}
      </div>

      {/* Active step detail */}
      {(() => {
        const s = ORG_STEPS[activeStep];
        return (
          <div style={{
            background: "rgba(255,255,255,0.02)", border: `1px solid ${s.color}33`,
            borderRadius: 14, padding: "20px 22px",
            borderLeft: `4px solid ${s.color}`,
          }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 28 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 9, color: s.color, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 3 }}>
                  Step {s.step} · Tier {s.tier}
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>{s.title}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7 }}>{s.desc}</div>
          </div>
        );
      })()}

      {/* Nav */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
        <button
          onClick={() => setActiveStep(s => Math.max(0, s - 1))}
          disabled={activeStep === 0}
          style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)",
            background: "transparent", color: activeStep === 0 ? "#334155" : "#94a3b8",
            fontSize: 11, cursor: activeStep === 0 ? "not-allowed" : "pointer" }}>
          ← Previous
        </button>
        <span style={{ fontSize: 10, color: "#475569", alignSelf: "center" }}>
          {activeStep + 1} of {ORG_STEPS.length}
        </span>
        <button
          onClick={() => setActiveStep(s => Math.min(ORG_STEPS.length - 1, s + 1))}
          disabled={activeStep === ORG_STEPS.length - 1}
          style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)",
            background: "transparent", color: activeStep === ORG_STEPS.length - 1 ? "#334155" : "#94a3b8",
            fontSize: 11, cursor: activeStep === ORG_STEPS.length - 1 ? "not-allowed" : "pointer" }}>
          Next →
        </button>
      </div>
    </div>
  );
}

function DealRoomSection() {
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {DEAL_ROOM_STEPS.map((step, i) => (
          <div key={i} style={{
            display: "flex", gap: 16, alignItems: "flex-start",
            padding: "16px 18px", borderRadius: 12,
            background: "rgba(124,111,255,0.04)",
            border: "1px solid rgba(124,111,255,0.15)",
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, flexShrink: 0,
              background: "rgba(124,111,255,0.1)", border: "1px solid rgba(124,111,255,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
            }}>{step.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{step.title}</div>
              <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>{step.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 10,
        background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#f5a623", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          ⚠ Important
        </div>
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
          Deal Room documents are provided under executed NDA only. Materials have not been approved by the SEC.
          This is not investment advice. InvestorQ does not verify document accuracy — all materials are provided by the organization.
        </div>
      </div>
    </div>
  );
}

function ValuationSection() {
  const [activeMethod, setActiveMethod] = useState(0);

  return (
    <div>
      <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10,
        background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.7 }}>
          InvestorQ applies an <b style={{ color: "#fff" }}>AICPA-aligned 4-Method valuation framework</b> for all assets.
          Tier A editorial estimates use public data + AI analysis. Tier B certified valuations incorporate verified financials under NDA for higher confidence.
        </div>
      </div>

      {/* Tier confidence comparison */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
        {[
          { tier: "A", label: "Editorial", confidence: 40, color: "#00d4aa", note: "Public data + AI" },
          { tier: "B", label: "Certified", confidence: 85, color: "#7c6fff", note: "Verified financials" },
        ].map(t => (
          <div key={t.tier} style={{ padding: "14px 16px", borderRadius: 12,
            background: "rgba(255,255,255,0.02)", border: `1px solid ${t.color}22` }}>
            <div style={{ fontSize: 9, color: t.color, fontWeight: 800, letterSpacing: "0.1em", marginBottom: 8 }}>
              TIER {t.tier} — {t.label.toUpperCase()}
            </div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, marginBottom: 6, overflow: "hidden" }}>
              <div style={{ width: `${t.confidence}%`, height: "100%", background: t.color, borderRadius: 3 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 10, color: "#475569" }}>{t.note}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: t.color }}>{t.confidence}% confidence</span>
            </div>
          </div>
        ))}
      </div>

      {/* 4 methods */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 16 }}>
        {VALUATION_METHODS.map((m, i) => (
          <button key={i} onClick={() => setActiveMethod(i)} style={{
            padding: "10px 6px", borderRadius: 10,
            border: `1px solid ${activeMethod === i ? m.color : "rgba(255,255,255,0.06)"}`,
            background: activeMethod === i ? `${m.color}15` : "rgba(255,255,255,0.02)",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 18, color: m.color, marginBottom: 4 }}>{m.icon}</div>
            <div style={{ fontSize: 8, color: activeMethod === i ? m.color : "#475569",
              fontWeight: 800, letterSpacing: "0.06em", lineHeight: 1.3 }}>
              {m.name}
            </div>
            <div style={{ fontSize: 9, color: m.color, fontWeight: 700, marginTop: 4 }}>{m.weight}</div>
          </button>
        ))}
      </div>

      {(() => {
        const m = VALUATION_METHODS[activeMethod];
        return (
          <div style={{ padding: "14px 16px", borderRadius: 12,
            background: `${m.color}08`, border: `1px solid ${m.color}22` }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 20, color: m.color }}>{m.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{m.name}</div>
                <div style={{ fontSize: 10, color: m.color }}>Weight: {m.weight}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.7 }}>{m.desc}</div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Main Guide Modal ──────────────────────────────────────────────────────────
export default function PrivateMarketGuide({ onClose }) {
  const [section, setSection] = useState("tiers");
  const overlayRef = useRef(null);

  // Close on escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const sections = [
    { id: "tiers",    label: "Tier Comparison",      icon: "◎◈◆" },
    { id: "orgflow",  label: "Org Flow",              icon: "🏆" },
    { id: "dealroom", label: "Deal Room",             icon: "🔒" },
    { id: "valuation",label: "How Valuations Work",  icon: "📊" },
  ];

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.88)",
        backdropFilter: "blur(20px)",
        zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        animation: "fadeIn 0.25s ease",
      }}>

      <div style={{
        background: "rgba(15,23,42,0.97)",
        border: "1px solid rgba(124,111,255,0.3)",
        borderRadius: 24,
        width: "100%", maxWidth: 680,
        maxHeight: "90vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 60px 140px rgba(124,111,255,0.2), 0 0 0 1px rgba(124,111,255,0.1)",
        animation: "slideUp 0.35s cubic-bezier(0.16,1,0.3,1)",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, rgba(124,111,255,0.15), rgba(0,212,170,0.08))",
          padding: "22px 28px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 900, color: "#7c6fff",
                background: "rgba(124,111,255,0.15)", border: "1px solid rgba(124,111,255,0.3)",
                padding: "2px 10px", borderRadius: 20, letterSpacing: "0.15em" }}>
                PLATFORM GUIDE
              </span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>
              InvestorQ Private Market
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              How it works for orgs and investors
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            color: "#94a3b8", cursor: "pointer", fontSize: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background="rgba(239,68,68,0.15)"; e.currentTarget.style.color="#ef4444"; }}
          onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.06)"; e.currentTarget.style.color="#94a3b8"; }}>
            ✕
          </button>
        </div>

        {/* Section tabs */}
        <div style={{
          display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)",
          padding: "0 28px", overflowX: "auto", flexShrink: 0,
        }}>
          {sections.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)} style={{
              padding: "12px 14px", background: "none", border: "none", cursor: "pointer",
              fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em",
              color: section === s.id ? "#7c6fff" : "#475569",
              borderBottom: section === s.id ? "2px solid #7c6fff" : "2px solid transparent",
              transition: "all 0.15s", whiteSpace: "nowrap",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontSize: 12 }}>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          {section === "tiers"    && <TierComparisonSection />}
          {section === "orgflow"  && <OrgFlowSection />}
          {section === "dealroom" && <DealRoomSection />}
          {section === "valuation"&& <ValuationSection />}
        </div>

        {/* Footer CTA */}
        <div style={{
          padding: "16px 28px",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          display: "flex", gap: 10, justifyContent: "space-between",
          alignItems: "center", flexShrink: 0,
          background: "rgba(0,0,0,0.2)",
        }}>
          <div style={{ fontSize: 10, color: "#475569" }}>
            Questions? <a href="mailto:info@investorq.io" style={{ color: "#7c6fff", textDecoration: "none" }}>info@investorq.io</a>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => { onClose(); window.dispatchEvent(new CustomEvent("investorq:navigate", { detail: "sports-org-portal" })); }}
              style={{ padding: "9px 18px", borderRadius: 10, border: "1px solid rgba(0,212,170,0.4)",
                background: "rgba(0,212,170,0.1)", color: "#00d4aa",
                fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
              Apply for Listing →
            </button>
            <button onClick={onClose} style={{ padding: "9px 18px", borderRadius: 10,
              border: "none", background: "linear-gradient(135deg,#7c6fff,#9b8fff)", color: "#fff",
              fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
              Explore the Market
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(30px) scale(0.97) } to { opacity:1; transform:translateY(0) scale(1) } }
      `}</style>
    </div>
  );
}